import React, {useEffect, useState} from "react";
import "./LandingPage.css";
import {apiGetLandingMoods, apiGetLandingThreads, apiGetLandingStats, apiGetLandingPage} from "./api/landing";
import {apiGetNews} from "./api/news";
import {apiGetMainCampaign} from "./api/campaigns";
import SappmModal from "./components/SappmModal";
import AboutModal from "./components/AboutModal";
import {navigateTo} from "./utils";
import SectionBoundary from "./landing/SectionBoundary";
import CustomSection from "./landing/CustomSection";
import MoodChart, {BelowThresholdMessage, moodWindowLabel} from "./landing/MoodChart";
import ThreadCards from "./landing/ThreadCards";
import {DEFAULT_PAGE, hasOwn} from "./landing/pageModel";
import {findBackground} from "./landing/backgrounds";
import SECTION_FIELDS from "./landing/sectionFields.json";
import {text, listOf, safeUrl, safeInternalUrl, youtubeIdOf, TitleText, InlineText} from "./landing/sectionText";
import {ExpandableYouTubeEmbed} from "./landing/YouTubeEmbed";

function MoodBadge({moods}) {
    if (!moods || moods.belowThreshold || moods.total === 0) return null;
    if (!Array.isArray(moods.moods) || !moods.results) return null; // réponse inattendue (erreur serveur…) : pas de pastille

    const levels = moods.moods;
    let sum = 0;
    let count = 0;
    for (const m of levels) {
        const c = moods.results[m.level] || 0;
        sum += m.level * c;
        count += c;
    }
    const avg = count > 0 ? (sum / count) : 0;
    const mainEmoji = avg >= 4.5 ? "🤩" : avg >= 3.5 ? "😊" : avg >= 2.5 ? "😐" : avg >= 1.5 ? "😟" : "😫";

    return (
        <div className="mood-badge">
            <span className="mood-badge-emoji">{mainEmoji}</span>
            <div className="mood-badge-text">
                <span className="mood-badge-label">Humeur ({moods.window_days || 15} j)</span>
                <span className="mood-badge-bar">
                    {levels.map(m => {
                        const c = moods.results[m.level] || 0;
                        const pct = Math.round((c / count) * 100);
                        if (pct === 0) return null;
                        const colors = {1: "#E74C3C", 2: "#E67E22", 3: "#F1C40F", 4: "#2ECC71", 5: "#0F6A71"};
                        return (
                            <span
                                key={m.level}
                                className="mood-badge-segment"
                                style={{width: `${pct}%`, backgroundColor: colors[m.level]}}
                                title={`${m.label}: ${pct}%`}
                            />
                        );
                    })}
                </span>
                <span className="mood-badge-count">{count} vote{count > 1 ? "s" : ""}</span>
            </div>
        </div>
    );
}

const DEFAULT_CAMPAIGN_LABEL = "Qui est de votre côté ?";

function TopBanner({onGoToApp, user, moods, onOpenSappm, showCampaign, campaignName, anchors}) {
    return (
        <div className="app-banner">
            <div className="app-banner-inner">
                <div className="banner-left">
                    <img
                        src="/paroles-agents-logo.png"
                        alt="SAPPM — Syndicat Autonome des Personnels des Services du Premier Ministre"
                        className="banner-logo"
                        onClick={onOpenSappm}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onOpenSappm();
                            }
                        }}
                    />
                    <div className="banner-brand">
                        <span className="banner-brand-title">Paroles d'Agents</span>
                        <span className="banner-brand-tagline-wrap">
                            <span className="banner-brand-tagline">Pour que chacun s'exprime&nbsp;!</span>
                            <svg className="signature-swash" viewBox="0 0 70 18" preserveAspectRatio="none" aria-hidden="true">
                                <path d="M0,15 C16,17 30,11 42,8 C54,5 62,2 70,0 L70,8 C62,9 54,11 42,13 C30,16 16,18 0,17 Z" fill="#FFC000" fillOpacity="0.85"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <div className="banner-center">
                    <span className="banner-bubbles" aria-hidden="true">
                        <svg width="34" height="28" viewBox="0 0 34 28" fill="none">
                            <path d="M3 4a3 3 0 0 1 3-3h13a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H10l-5 5v-5H6a3 3 0 0 1-3-3V4Z" fill="#EAF6F4"/>
                            <path d="M31 11a2.4 2.4 0 0 0-2.4-2.4H17.6a2.4 2.4 0 0 0-2.4 2.4v6.4a2.4 2.4 0 0 0 2.4 2.4h7.6l4 3.6v-3.6h0a2.4 2.4 0 0 0 2.4-2.4V11Z" fill="#FFE9AE"/>
                        </svg>
                    </span>
                    <div className="banner-center-text">
                        <span className="banner-headline">Réseau d'échange, de propositions et d'alertes</span>
                        <span className="banner-subtext">Ensemble, faisons entendre la voix des agents 💚</span>
                    </div>
                </div>
                <MoodBadge moods={moods}/>
                <div className="banner-right">
                    <button className="btn-banner-cta" onClick={onGoToApp}>
                        Accéder au réseau
                    </button>
                </div>
            </div>
            <nav className="app-banner-nav">
                {anchors.has("comment") && <a href="#comment">Comment ça marche</a>}
                {anchors.has("outils") && <a href="#outils">Outils bien-être</a>}
                {anchors.has("ambiance") && <a href="#ambiance">Ambiance collective</a>}
                {anchors.has("sujets") && <a href="#sujets">Sujets en cours</a>}
                <a href="/actualites" onClick={e => { e.preventDefault(); navigateTo("/actualites"); }}>Actualités</a>
                {/* Vrai lien (rechargement complet) : /campagne/ est servi par le backend, pas par l'appli React. */}
                {showCampaign && <a href="/campagne/" className="nav-campaign" title={campaignName}>{campaignName}</a>}
                <a href="#syndicat" className="nav-sappm" onClick={e => { e.preventDefault(); onOpenSappm(); }}>Le SAPPM</a>
            </nav>
        </div>
    );
}

const PHONE_NEWS_MAX = 10;
const PHONE_NEWS_SCROLL_MIN = 5;
const PHONE_NEWS_SECONDS_PER_TITLE = 3;

// Titres des actualités façon prompteur : à partir de PHONE_NEWS_SCROLL_MIN titres, la liste
// est rendue deux fois à la suite et l'animation CSS la translate de -50 %, ce qui boucle sans
// saut. La copie est masquée aux lecteurs d'écran et sortie de la tabulation.
function PhoneNewsTicker({news}) {
    const items = news.slice(0, PHONE_NEWS_MAX);
    const scrolling = items.length >= PHONE_NEWS_SCROLL_MIN;

    const renderList = isCopy => (
        <ul className="phone-news-list" aria-hidden={isCopy ? "true" : undefined}>
            {items.map(article => (
                <li key={article.slug}>
                    <a
                        href={`/actualites/${article.slug}`}
                        tabIndex={isCopy ? -1 : undefined}
                        onClick={e => { e.preventDefault(); navigateTo(`/actualites/${article.slug}`); }}
                    >
                        {article.title}
                    </a>
                </li>
            ))}
        </ul>
    );

    return (
        <div className={`phone-news${scrolling ? " is-scrolling" : ""}`}>
            <div
                className="phone-news-track"
                style={scrolling ? {"--ticker-duration": `${items.length * PHONE_NEWS_SECONDS_PER_TITLE}s`} : undefined}
            >
                {renderList(false)}
                {scrolling && renderList(true)}
            </div>
        </div>
    );
}

function HeroSection({data = {}, onRegister, memberCount, news, showHowItWorks = true}) {
    const hasNews = Array.isArray(news) && news.length > 0;

    return (
        <section className="hero">
            <div className="wrap hero-grid">
                <div>
                    <span className="eyebrow">{text(data, "eyebrow", "Notre réseau d'échange, de propositions et d'alertes")}</span>
                    <h1 className="serif"><TitleText value={data.title} fallback="Votre voix compte {Faites-la entendre} — anonymement" accentTag="em"/></h1>
                    <div className="anonymat-badge">
                        <span className="anonymat-icon">🔒</span>
                        <span>{text(data, "badge_text", "Anonyme par défaut — votre hiérarchie ne voit jamais votre nom")}</span>
                    </div>
                    <p className="lede">
                        {text(data, "lede", "Un espace pour dire ce qui ne va pas, suivre l'ambiance collective et peser, ensemble, sur les dossiers traités en CSA, Groupe de travail, FSSSCT, CAP et CCP — avant que les décisions ne soient prises sans vous.")}
                    </p>
                    <div className="hero-ctas">
                        <button onClick={onRegister} className="btn btn-primary">{text(data, "primary_label", "S'inscrire — c'est gratuit")}</button>
                        {showHowItWorks && <a href="#comment" className="btn btn-ghost">{text(data, "secondary_label", "Comment ça marche")}</a>}
                    </div>
                    <div className="social-proof">
                        <span className="social-proof-avatars" aria-hidden="true">●●●</span>
                        {memberCount > 0
                            ? <span><strong>{memberCount} agent{memberCount > 1 ? "s" : ""}</strong> déjà inscrit{memberCount > 1 ? "s" : ""} — rejoignez-les</span>
                            : <span>Soyez parmi les premiers à lancer Paroles d'Agents</span>}
                    </div>
                    <div className="trust-line">
                        <span className="dot"></span>
                        {text(data, "trust_line", "Porté par vos représentants du personnel élus — indépendant de toute hiérarchie.")}
                    </div>
                </div>

                <div className="phone-stage">
                    <div className="phone-device">
                        <div className="phone">
                            <div className="phone-statusbar" aria-hidden="true">
                                <span>9:41</span>
                                <span>●●● 📶 🔋</span>
                            </div>
                            <div className="phone-app-header">
                                <div className="seal-anim serif">P</div>
                                <span className="phone-app-title">{text(data, "phone_title", "Dernières actualités")}</span>
                            </div>
                            {hasNews ? (
                                <PhoneNewsTicker news={news}/>
                            ) : (
                                <>
                                    <div className="phone-card">
                                        <span className="tag">SUJET ACTIF</span>
                                        <p>RIFSEEP 2026 — donnez votre avis avant le CSA du 14 juillet.</p>
                                    </div>
                                    <div className="phone-card">
                                        <span className="tag">ANONYME</span>
                                        <p>Votre message a été transmis à un représentant. Réponse sous 48h.</p>
                                    </div>
                                </>
                            )}
                            <div className="phone-home-indicator" aria-hidden="true"></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const SERVICES = [
    {
        emoji: "🔒",
        title: "Inscription anonyme",
        text: "Votre email professionnel sert uniquement à vérifier votre appartenance aux services du Premier Ministre (SPM) et à interdire toute inscription étrangère au ministère — puis vous choisissez un pseudonyme parmi ceux que l'on vous propose (animal + adjectif), que personne ne peut relier à votre nom.",
    },
    {
        emoji: "😊",
        title: "Humeur du jour",
        text: "Un tap chaque jour pour dire comment vous allez. Vos représentants suivent l'ambiance collective, jamais votre humeur individuelle.",
    },
    {
        emoji: "🗳️",
        title: "Sondages",
        text: "Votre collectif vous consulte sur les sujets en cours. Vous votez en un clic ; les résultats ne s'affichent qu'au-delà d'un seuil de participants, jamais un vote isolé identifiable.",
    },
    {
        emoji: "💡",
        title: "Idées",
        text: "Proposez un changement avec l'amorce « Et si chez nous on… ». Vous choisissez votre niveau de visibilité, et vos collègues peuvent soutenir, partager et commenter votre proposition.",
    },
    {
        emoji: "💬",
        title: "Discussions",
        text: "Rejoignez un fil par sujet ou par service pour échanger avec vos collègues, sous votre pseudonyme.",
    },
    {
        emoji: "🤝",
        title: "Invitez un collègue",
        text: "Générez un lien depuis l'app et partagez-le par email ou message. Aucune donnée personnelle n'est requise pour vous inscrire.",
    },
    {
        emoji: "🛟",
        title: "Demande d'aide",
        text: (
            <>
                Un canal confidentiel direct avec un{" "}
                <span
                    className="info-term"
                    title="Un représentant du personnel est un agent élu par ses collègues (via le SAPPM) pour porter la voix des agents dans les instances officielles de la fonction publique d'État — CSA, FSSSCT, CAP, CCP. Il ou elle intervient en cas de conflit, alerte sur les conditions de travail et défend les agents, indépendamment de la hiérarchie."
                >
                    représentant du personnel
                </span>
                , pour les situations difficiles — harcèlement, mal-être, conflit. Toujours sous votre pseudonyme.
            </>
        ),
    },
];

const EPISODE_1_VIDEO_ID = "JmGWfeloX_Y";
const TUTO_INSCRIPTION_VIDEO_ID = "T-RHLnszgus";
const SERIES_PLAYLIST_ID = "PLKbq7ecqHWJ8";
const SERIES_EPISODE_COUNT = 3;

function VideoSection({data = {}}) {
    const [showPlaylist, setShowPlaylist] = useState(false);

    return (
        <section className="video-section">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{showPlaylist ? "La série complète" : `Épisode 1/${SERIES_EPISODE_COUNT}`}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="Découvrez Paroles d'Agents {en vidéo}"/></h2>
                    <p>{showPlaylist ? text(data, "intro_series", `Les ${SERIES_EPISODE_COUNT} épisodes de notre série, à la suite.`) : text(data, "intro", "Le premier épisode de notre série — pour comprendre le réseau en quelques minutes.")}</p>
                </div>
                <ExpandableYouTubeEmbed
                    videoId={showPlaylist ? undefined : youtubeIdOf(data.video_id, EPISODE_1_VIDEO_ID)}
                    playlistId={showPlaylist ? youtubeIdOf(data.playlist_id, SERIES_PLAYLIST_ID) : undefined}
                    title={showPlaylist ? "Paroles d'Agents — la série" : "Paroles d'Agents — Épisode 1"}
                />
                <button type="button" className="video-expand-btn" onClick={() => setShowPlaylist(v => !v)}>
                    {showPlaylist ? text(data, "collapse_label", "← Revenir à l'épisode 1") : text(data, "expand_label", "Voir toute la série →")}
                </button>
            </div>
        </section>
    );
}

function ServicesSection({data = {}}) {
    return (
        <section id="comment">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{text(data, "eyebrow", "Comment ça marche")}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="Voici les services {auxquels vous allez accéder}"/></h2>
                    <p>{text(data, "intro", "Sept outils simples, pensés pour votre anonymat, disponibles dès votre inscription — voici comment vous en servir.")}</p>
                </div>
                <div className="services-grid">
                    {listOf(data, "items", SERVICES).map((s, i) => (
                        <div key={i} className="service-card">
                            <span className="service-emoji" aria-hidden="true">{s.emoji}</span>
                            <h3>{s.title}</h3>
                            <p>{s.text}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Textes d'origine de la rubrique Syndicat ; remplacés par data.paragraphs / data.badges si l'admin en fournit.
const SYNDICAT_PARAGRAPHS = [
    {
        "text": "Le SAPPM (Syndicat Autonome des Personnels des Services du Premier Ministre) est l'organisation qui porte ce réseau — des agents élus par leurs collègues, pas une direction, pas une structure RH."
    },
    {
        "text": "Cette indépendance est ce qui rend la confidentialité crédible : ce que vous partagez sert votre défense collective, jamais l'évaluation de votre hiérarchie."
    },
    {
        "text": "Pour les situations les plus difficiles — harcèlement, mal-être, conflit — un canal confidentiel dédié vous met directement en lien avec un représentant, sous votre pseudonyme anonyme."
    }
];
const SYNDICAT_BADGES = [
    {
        "text": "SPM · DILA · INSP · SGDSN"
    },
    {
        "text": "Élections décembre 2026"
    }
];

function SyndicatSection({data = {}}) {
    return (
        <section className="syndicat-section" id="syndicat">
            <div className="wrap syndicat-grid">
                <div className="poster-frame">
                    <img src={safeInternalUrl(data.poster_url, "/sappm-syndicat-poster.jpg")} alt="Poster SAPPM"/>
                </div>
                <div className="syndicat-copy">
                    <span className="eyebrow">{text(data, "eyebrow", "Qui porte Paroles d'Agents")}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="Un syndicat {autonome}, élu par vous"/></h2>
                    {listOf(data, "paragraphs", SYNDICAT_PARAGRAPHS).map((p, i) => <p key={i}><InlineText value={p.text} fallback=""/></p>)}
                    <div className="syndicat-badges">
                        {listOf(data, "badges", SYNDICAT_BADGES).map((b, i) => <span key={i} className="syndicat-badge">{b.text}</span>)}
                    </div>
                </div>
            </div>
        </section>
    );
}

function DashboardSection({data = {}, moods}) {
    if (!moods) {
        return (
            <section id="ambiance" className="dashboard">
                <div className="wrap"><p>Chargement…</p></div>
            </section>
        );
    }

    return (
        <section id="ambiance" className="dashboard">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{text(data, "eyebrow", "Mesure collective & anonyme")}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="L'ambiance, {vue} d'ensemble"/></h2>
                    <p>{text(data, "intro", "Un indicateur d'alerte précoce pour vos représentants — jamais un outil de surveillance individuelle.")}</p>
                </div>
                <div className="dash-card">
                    <div className="dash-meta">
                        <span className="mono">Humeur des {moodWindowLabel(moods)}</span>
                        <h3 className="serif">{text(data, "card_title", "Climat de travail, tous services")}</h3>
                        <p>{text(data, "card_text", "Calculé à partir des réponses anonymisées au pouls quotidien. Sert à documenter une alerte FSSSCT ou un point d'ordre du jour en CSA.")}</p>
                        <div className="threshold-note">
                            Aucune donnée n'est affichée en dessous de {moods.threshold} répondant·es,
                            pour qu'aucune réponse individuelle ne soit jamais identifiable.
                        </div>
                    </div>
                    <div>
                        {moods.belowThreshold || !moods.results ? <BelowThresholdMessage moods={moods}/> : <MoodChart moods={moods}/>}
                    </div>
                </div>
            </div>
        </section>
    );
}

// La rubrique d'origine affiche toujours les 3 fils les plus actifs ; le bloc libre choisit 3 ou 6 (voir liveBlocks).
const THREADS_SECTION_COUNT = 3;

function ThreadsSection({data = {}, threads: allThreads}) {
    const threads = allThreads.slice(0, THREADS_SECTION_COUNT);
    if (threads.length === 0) return null;

    return (
        <section id="sujets">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{text(data, "eyebrow", "Avant que la décision ne tombe")}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="Donnez votre avis, {pas} seulement vos représentants"/></h2>
                    <p>{text(data, "intro", "Chaque dossier en cours d'instruction a son fil. Vos réactions deviennent la matière que vos représentants portent en instance.")}</p>
                </div>
                <ThreadCards threads={threads}/>
            </div>
        </section>
    );
}

// Étapes d'inscription d'origine (** = gras) ; remplacées par data.steps si l'admin en fournit.
const SIGNUP_STEPS = [
    {
        "text": "Cliquez sur **« Accéder au réseau »** en haut de cette page"
    },
    {
        "text": "Choisissez **« Créer un compte »** et entrez votre adresse email professionnelle — elle sert uniquement à vérifier que vous appartenez aux services"
    },
    {
        "text": "L'appli vous propose **trois pseudonymes** composés d'un animal et d'un adjectif — choisissez celui qui vous plaît. Ce pseudo protège votre anonymat : personne ne peut faire le lien avec votre vrai nom"
    },
    {
        "text": "Créez votre mot de passe — et c'est tout. Vous êtes dans le réseau"
    }
];

function DownloadSection({data = {}, onGoToApp}) {
    return (
        <section id="telecharger">
            <div className="wrap">
                <div className="promo">
                    <div>
                        <span className="eyebrow">{text(data, "eyebrow", "Plus nous sommes nombreux, plus notre voix porte")}</span>
                        <h2 className="serif"><TitleText value={data.title} fallback="Inscrivez-vous, {invitez} un.e collègue"/></h2>
                        <p className="lede">
                            {text(data, "lede", "Un dossier porté par 50 voix pèse plus qu'un dossier porté par une seule. Chaque agent qui rejoint Paroles d'Agents renforce ce que vos représentants peuvent obtenir en instance.")}
                        </p>
                        <div className="signup-steps">
                            {listOf(data, "steps", SIGNUP_STEPS).map((step, i) => (
                                <div key={i} className="signup-step">
                                    <span className="signup-step-num">{i + 1}</span>
                                    <span><InlineText value={step.text} fallback=""/></span>
                                </div>
                            ))}
                        </div>
                        <div className="invite">
                            <span className="dot"></span>
                            {text(data, "invite_text", "Partagez le lien à un collègue — aucune donnée personnelle requise.")}
                        </div>
                        <button className="btn btn-primary" onClick={onGoToApp} style={{marginTop: 20}}>
                            {text(data, "button_label", "Accéder au réseau")}
                        </button>
                    </div>
                    <div className="qr-block">
                        <div className="tuto-video">
                            <p className="tuto-video-title">{text(data, "tuto_title", "Inscription pas à pas")}</p>
                            <ExpandableYouTubeEmbed
                                videoId={youtubeIdOf(data.tuto_video_id, TUTO_INSCRIPTION_VIDEO_ID)}
                                title={text(data, "tuto_title", "Inscription pas à pas")}
                                className="tuto-video-embed"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FooterSection() {
    const [showAbout, setShowAbout] = useState(false);

    function navigate(path) {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <footer>
            <div className="wrap footer-inner">
                <span>Paroles d'Agents — un réseau du SAPPM (Syndicat Autonome des Personnels des Services du Premier Ministre)</span>
                <span>
                    <a href="/confidentialite" onClick={e => { e.preventDefault(); navigate("/confidentialite"); }}>Confidentialité &amp; RGPD</a>
                    {" · "}
                    <a href="/mentions" onClick={e => { e.preventDefault(); navigate("/mentions"); }}>Mentions légales</a>
                    {" · "}
                    <a href="/cgu" onClick={e => { e.preventDefault(); navigate("/cgu"); }}>CGU</a>
                    {" · "}
                    <a href="/actualites" onClick={e => { e.preventDefault(); navigate("/actualites"); }}>Actualités</a>
                    {" · "}
                    <a href="/contact" onClick={e => { e.preventDefault(); navigate("/contact"); }}>Contact</a>
                    {" · "}
                    <a href="/a-propos" onClick={e => { e.preventDefault(); setShowAbout(true); }}>À propos</a>
                </span>
            </div>
            {showAbout && <AboutModal onClose={() => setShowAbout(false)}/>}
        </footer>
    );
}

const TESTIMONIALS = [
    {
        quote: "J'ai enfin pu dire ce qui ne va pas sans craindre les conséquences.",
        pseudo: "Renard prudent",
        service: "SPM",
    },
    {
        quote: "Mes représentants ont porté mon problème en CSA. Je n'aurais jamais osé seul.",
        pseudo: "Hibou curieux",
        service: "DILA",
    },
    {
        quote: "L'humeur du jour m'a montré que je n'étais pas le seul à trouver ça difficile.",
        pseudo: "Castor serein",
        service: "INSP",
    },
];

function TestimonialsSection({data = {}}) {
    return (
        <section className="testimonials-section">
            <div className="wrap">
                <div className="testimonials-grid">
                    {listOf(data, "items", TESTIMONIALS).map((t, i) => (
                        <div key={i} className="testimonial-card">
                            <span className="testimonial-quote-mark" aria-hidden="true">"</span>
                            <p className="testimonial-text">{t.quote}</p>
                            <div className="testimonial-author">
                                <span className="testimonial-pseudo">{t.pseudo}</span>
                                <span className="testimonial-service mono">{t.service}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="testimonials-disclaimer">{text(data, "disclaimer", "Exemples fictifs présentés à titre d'illustration.")}</p>
            </div>
        </section>
    );
}

const WELLBEING_TESTS = [
    {
        emoji: "📋",
        title: "Travaillez-vous dans une maison de fous ?",
        subtitle: "Test QVT de Martin Wehle",
        meta: "40 questions · 5 minutes",
        description: "Évaluez objectivement la qualité de vie au travail dans votre service grâce à un outil reconnu.",
        button_label: "Faire le test",
        path: "/qvt",
    },
    {
        emoji: "🧠",
        title: "Évaluez votre santé mentale",
        subtitle: "Questionnaire GHQ-12",
        meta: "12 questions · 3 minutes · Anonyme",
        description: "Un outil clinique validé pour mesurer l'impact du travail sur votre bien-être psychologique.",
        button_label: "Commencer le test",
        path: "/ghq12",
    },
    {
        emoji: "🤖",
        title: "IA et agents : où en êtes-vous, humainement parlant ?",
        subtitle: "Parcours adaptatif",
        meta: "15 questions · 6 minutes · Anonyme",
        description: "Un parcours qui s'adapte à vos réponses, de la découverte de l'IA aux enjeux de cyberdéfense.",
        button_label: "Commencer le parcours",
        path: "/ia-agents",
    },
];

// Accent de couleur de chaque carte « bien-être », selon sa position (repris en boucle au-delà de trois).
const WELLBEING_ACCENTS = ["butter", "mint", "sage"];

function WellbeingSection({data = {}}) {
    function navigate(path) {
        if (path.startsWith("https://")) {
            window.location.assign(path);
            return;
        }
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <section id="outils">
            <div className="wrap">
                <div className="section-head">
                    <span className="eyebrow">{text(data, "eyebrow", "Ressources bien-être")}</span>
                    <h2 className="serif"><TitleText value={data.title} fallback="Évaluez, {comprenez}, agissez"/></h2>
                    <p>{text(data, "intro", "Des outils validés pour mettre des mots sur ce que vous ressentez au travail — anonymes et sans inscription.")}</p>
                </div>
                <div className="wellbeing-grid">
                    {listOf(data, "items", WELLBEING_TESTS).map((t, i) => {
                        const accent = WELLBEING_ACCENTS[i % WELLBEING_ACCENTS.length];
                        return (
                        <div key={i} className={`wellbeing-card wellbeing-card--${accent}`}>
                            <div className="wellbeing-card-top">
                                <span className="wellbeing-emoji">{t.emoji}</span>
                                <div>
                                    <h3 className="serif">{t.title}</h3>
                                    <span className="wellbeing-subtitle">{t.subtitle}</span>
                                </div>
                            </div>
                            <span className="wellbeing-meta mono">{t.meta}</span>
                            <p className="wellbeing-desc">{t.description}</p>
                            {safeUrl(t.path, null) && (
                                <button className={`wellbeing-btn wellbeing-btn--${accent}`} onClick={() => navigate(t.path)}>
                                    {t.button_label}
                                </button>
                            )}
                        </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// --- Moteur de rubriques : la landing est une liste ordonnée de rubriques (voir landing/pageModel.js) ---

const SECTION_COMPONENTS = {
    hero: ({data, ctx}) => <HeroSection data={data} onRegister={ctx.onRegister} memberCount={ctx.memberCount} news={ctx.news} showHowItWorks={ctx.anchors.has("comment")}/>,
    video: ({data}) => <VideoSection data={data}/>,
    services: ({data}) => <ServicesSection data={data}/>,
    signup: ({data, ctx}) => <DownloadSection data={data} onGoToApp={ctx.onRegister}/>,
    testimonials: ({data}) => <TestimonialsSection data={data}/>,
    threads: ({data, ctx}) => <ThreadsSection data={data} threads={ctx.threads}/>,
    wellbeing: ({data}) => <WellbeingSection data={data}/>,
    mood: ({data, ctx}) => <DashboardSection data={data} moods={ctx.moods}/>,
    syndicat: ({data}) => <SyndicatSection data={data}/>,
    custom: (props) => <CustomSection section={props.section} ctx={props.ctx}/>,
};

// Au-delà de ce délai sans réponse de l'API, la landing par défaut est affichée (jamais de page vide).
const LANDING_LOADING_TIMEOUT_MS = 1500;

// Page à afficher : celle reçue dès qu'elle est exploitable (même sans rubrique : tout masquer est un choix de
// l'admin, et le serveur garde un bouton d'inscription durable), sinon la landing par défaut.
function choosePage(candidate) {
    return candidate && Array.isArray(candidate.sections) ? candidate : DEFAULT_PAGE;
}

// Ancres (#comment, #outils…) des rubriques présentes : les liens du bandeau vers une rubrique absente disparaissent.
function anchorsOf(page) {
    return new Set(page.sections.map(s => hasOwn(SECTION_FIELDS, s.type) && SECTION_FIELDS[s.type].anchor).filter(Boolean));
}

function renderSections(page, ctx) {
    return page.sections
        .filter(s => hasOwn(SECTION_COMPONENTS, s.type))
        .map(s => {
            const Component = SECTION_COMPONENTS[s.type];
            const section = (
                <SectionBoundary key={s.id} name={s.type}>
                    <Component data={s.data || {}} ctx={ctx} section={s}/>
                </SectionBoundary>
            );
            // Une rubrique libre gère son fond elle-même (settings.background) ; les autres sont habillées ici.
            const background = s.type === "custom" ? undefined : findBackground(s.background);
            if (!background) return section;
            return (
                <div key={s.id} className={`section-bg custom-bg--${background.id}${background.dark ? " section-bg--dark" : ""}`}>
                    {section}
                </div>
            );
        });
}

export default function LandingPage({user, onGoToApp, onRegister, page}) {
    const [moods, setMoods] = useState(null);
    const [threads, setThreads] = useState([]);
    const [memberCount, setMemberCount] = useState(0);
    const [news, setNews] = useState([]);
    const [sappmOpen, setSappmOpen] = useState(false);
    const [campaignActive, setCampaignActive] = useState(false);
    const [campaignName, setCampaignName] = useState(DEFAULT_CAMPAIGN_LABEL);
    // Page publiée : undefined = pas encore reçue, null = pas de réponse exploitable. Dans les deux cas la landing
    // par défaut (celle d'origine) est affichée : la page n'est jamais vide.
    const [published, setPublished] = useState(undefined);

    useEffect(() => {
        // Seule une vraie réponse d'humeur est gardée : un corps d'erreur (500, 429…) ne doit pas faire planter la landing.
        apiGetLandingMoods().then(data => setMoods(data && Array.isArray(data.moods) ? data : null)).catch(() => {});
        apiGetLandingThreads(6).then(data => setThreads(Array.isArray(data) ? data : [])).catch(() => {});
        apiGetLandingStats().then(data => setMemberCount(data.total || 0) /* total null : sous le seuil d'affichage */).catch(() => {});
        apiGetNews({featuredFirst: true, limit: PHONE_NEWS_MAX}).then(data => setNews(Array.isArray(data.items) ? data.items : []));
        apiGetMainCampaign().then(data => {
            setCampaignActive(Boolean(data && data.active));
            // Un serveur plus ancien ne renvoie pas de nom : on garde alors l'intitulé d'origine.
            if (data && typeof data.name === "string" && data.name.trim()) setCampaignName(data.name.trim());
        });
    }, []);

    useEffect(() => {
        if (page) return undefined;
        let cancelled = false;
        const timeout = setTimeout(() => { if (!cancelled) setPublished(current => (current === undefined ? null : current)); }, LANDING_LOADING_TIMEOUT_MS);
        // Promise.resolve : une API simulée qui ne renvoie pas de promesse ne doit pas casser la page.
        Promise.resolve(apiGetLandingPage())
            // Une réponse arrivée après le délai est ignorée : le contenu affiché ne change pas sous les yeux de l'agent.
            .then(data => { if (!cancelled) setPublished(current => (current === undefined ? (data && data.page ? data.page : null) : current)); })
            .catch(() => { if (!cancelled) setPublished(null); });
        return () => { cancelled = true; clearTimeout(timeout); };
    }, [page]);

    // Tant que la page publiée n'est pas arrivée (et avant le délai), aucune rubrique : afficher d'abord la landing par défaut
    // ferait clignoter ordre, textes et rubriques masquées ou planifiées.
    const loading = !page && published === undefined;
    const effectivePage = loading ? {schema: 1, sections: []} : choosePage(page || published);
    const anchors = anchorsOf(effectivePage);
    const ctx = {onRegister: onRegister || onGoToApp, memberCount, news, threads, moods, anchors};

    function openSappm() { setSappmOpen(true); }
    function closeSappm() { setSappmOpen(false); }
    function learnMoreSappm() {
        setSappmOpen(false);
        window.history.pushState({}, "", "/sappm");
        window.dispatchEvent(new PopStateEvent("popstate"));
    }

    return (
        <div className="landing-page">
            <TopBanner user={user} onGoToApp={onGoToApp} moods={moods} onOpenSappm={openSappm} showCampaign={campaignActive} campaignName={campaignName} anchors={anchors}/>
            <SappmModal open={sappmOpen} onClose={closeSappm} onLearnMore={learnMoreSappm}/>
            {loading && <div className="landing-loading" aria-busy="true" style={{minHeight: "60vh"}}/>}
            {renderSections(effectivePage, ctx)}
            <FooterSection/>
        </div>
    );
}
